import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from 'components/UI/Modal';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Button from 'components/UI/Button';
import { Text, Main, Background, Border } from 'styles/themeColors';
import { FontSize } from 'styles/themeSizes';
import Tabs from 'components/UI/Tabs';
import Input from 'components/UI/Input';
import { Space } from 'styles/themeSpaces';
import { authenticateAzureAD, fetchSingleMaterial, fetchBatchMaterials } from 'requests/buyNowApiTesting';
import { useAuth } from 'lib/Auth';
import { v4 } from 'uuid';
import { API_LEVEL, BUY_NOW_LABELS, POSTMAN_BRANDS_COLLECTION_PATH, POSTMAN_BRANDS_ENV_PATH, POSTMAN_DOWNLOAD_LINK, POSTMAN_VENDORS_COLLECTION_PATH, POSTMAN_VENDORS_ENV_PATH } from 'constants/buyNow';
import Tooltip from 'components/UI/Tooltip';

const InstructionsList = styled.ol`
  margin: 0;
  padding-left: 20px;
  color: ${Text.primary};
  font-size: ${FontSize.medium};
  line-height: 1.6;

  li {
    margin-bottom: 12px;
  }

  strong {
    font-weight: 600;
  }

  code {
    background-color: #f5f5f5;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    font-size: ${FontSize.small};
  }
`;

const SectionTitle = styled.div<any>`
  color: ${Text.primary};
  font-size: ${FontSize.big};
  font-weight: 600;
  margin: 20px 0 10px 0;
`;

const DownloadButton = styled(Button)`
  margin-top: 20px;
`;

const ScrollableContainer = styled(FlexboxContainer)`
  padding-bottom: 20px;
`;

const ApiSection = styled.div`
  background-color: ${Background.white};
  border: 1px solid ${Border.light};
  border-radius: 6px;
  padding: ${Space.sp2x};
`;

const ApiSectionTitle = styled.h6`
  color: ${Text.primary};
  font-size: ${FontSize.medium};
  font-weight: 600;
  margin: 0 0 ${Space.spBase} 0;
`;

const ResponseArea = styled.pre`
  background-color: #f5f5f5;
  border: 1px solid ${Border.light};
  border-radius: 4px;
  padding: ${Space.spBase};
  max-height: 300px;
  overflow: auto;
  font-size: ${FontSize.small};
  font-family: 'Courier New', monospace;
  white-space: pre-wrap;
  word-wrap: break-word;
  margin-top: ${Space.spBase};
`;

const ErrorMessage = styled.div`
  color: ${Main.error};
  font-size: ${FontSize.small};
  margin-top: ${Space.spHalf};
`;

const SuccessMessage = styled.div`
  color: ${Main.success};
  font-size: ${FontSize.small};
  margin-top: ${Space.spHalf};
`;

interface Props {
  show: boolean;
  onClose: () => void;
  level: string;
  credentials?: {
    clientId: string;
    clientSecret: string;
    clientUrl: string;
    buyNowConsumerKey: string;
    brandCode: string;
  } | null;
}

const BuyNowConfigModal: React.FC<Props> = ({ show, onClose, level, credentials }) => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState(0);

  // Determine labels and paths based on level
  const isVendor = level === API_LEVEL.VENDOR;
  const keyLabel = isVendor ? BUY_NOW_LABELS.VENDOR_KEY_LABEL : BUY_NOW_LABELS.BRAND_KEY_LABEL;
  const codeLabel = isVendor ? BUY_NOW_LABELS.VENDOR_CODE : BUY_NOW_LABELS.BRAND_CODE;
  const collectionPath = isVendor ? POSTMAN_VENDORS_COLLECTION_PATH : POSTMAN_BRANDS_COLLECTION_PATH;
  const envName = isVendor ? BUY_NOW_LABELS.VENDOR_ENV_NAME : BUY_NOW_LABELS.BRAND_ENV_NAME;
  const envFileName = isVendor ? POSTMAN_VENDORS_ENV_PATH : POSTMAN_BRANDS_ENV_PATH;
  const codeKey = isVendor ? BUY_NOW_LABELS.VENDOR_CODE_KEY : BUY_NOW_LABELS.BRAND_CODE_KEY;
  const keyKey = isVendor ? BUY_NOW_LABELS.VENDOR_KEY : BUY_NOW_LABELS.BRAND_KEY;

  // Azure AD state
  const [azureClientId, setAzureClientId] = useState('');
  const [azureClientSecret, setAzureClientSecret] = useState('');
  const [azureResponse, setAzureResponse] = useState('');
  const [azureError, setAzureError] = useState('');
  const [azureLoading, setAzureLoading] = useState(false);
  const [accessToken, setAccessToken] = useState('');

  // Single Material state
  const [singleBaseUrl, setSingleBaseUrl] = useState('');
  const [singleBrandCode, setSingleBrandCode] = useState('');
  const [singleBrandKey, setSingleBrandKey] = useState('');
  const [materialNumber, setMaterialNumber] = useState('');
  const [singleCountry, setSingleCountry] = useState('');
  const [singleMaterialResponse, setSingleMaterialResponse] = useState('');
  const [singleMaterialError, setSingleMaterialError] = useState('');
  const [singleMaterialLoading, setSingleMaterialLoading] = useState(false);

  // Batch Material state
  const [batchBaseUrl, setBatchBaseUrl] = useState('');
  const [batchBrandCode, setBatchBrandCode] = useState('');
  const [batchBrandKey, setBatchBrandKey] = useState('');
  const [materialNumbers, setMaterialNumbers] = useState('');
  const [batchCountry, setBatchCountry] = useState('');
  const [batchMaterialResponse, setBatchMaterialResponse] = useState('');
  const [batchMaterialError, setBatchMaterialError] = useState('');
  const [batchMaterialLoading, setBatchMaterialLoading] = useState(false);

  // Update form fields when credentials change
  useEffect(() => {
    if (credentials) {
      setAzureClientId(credentials.clientId || '');
      setAzureClientSecret(credentials.clientSecret || '');
      setSingleBaseUrl(credentials.clientUrl || '');
      setSingleBrandCode(credentials.brandCode || '');
      setSingleBrandKey(credentials.buyNowConsumerKey || '');
      setBatchBaseUrl(credentials.clientUrl || '');
      setBatchBrandCode(credentials.brandCode || '');
      setBatchBrandKey(credentials.buyNowConsumerKey || '');
    } else {
      // Reset all form fields when credentials are null
      setAzureClientId('');
      setAzureClientSecret('');
      setSingleBaseUrl('');
      setSingleBrandCode('');
      setSingleBrandKey('');
      setBatchBaseUrl('');
      setBatchBrandCode('');
      setBatchBrandKey('');

      // Reset responses and errors
      setAzureResponse('');
      setAzureError('');
      setAccessToken('');
      setSingleMaterialResponse('');
      setSingleMaterialError('');
      setMaterialNumber('');
      setSingleCountry('');
      setBatchMaterialResponse('');
      setBatchMaterialError('');
      setMaterialNumbers('');
      setBatchCountry('');

      // Reset to Instructions tab
      setActiveTab(0);
    }
  }, [credentials]);

  // Reset API testing form responses when modal is closed
  useEffect(() => {
    if (!show) {
      // Reset Azure AD responses
      setAzureResponse('');
      setAzureError('');
      setAccessToken('');

      // Reset Single Material responses
      setSingleMaterialResponse('');
      setSingleMaterialError('');
      setMaterialNumber('');
      setSingleCountry('');

      // Reset Batch Material responses
      setBatchMaterialResponse('');
      setBatchMaterialError('');
      setMaterialNumbers('');
      setBatchCountry('');

      // Reset to Instructions tab
      setActiveTab(0);
    }
  }, [show]);

  const handleDownloadCollection = () => {
    // Simply download the collection file as-is without replacing variables
    const link = document.createElement('a');
    link.href = collectionPath;
    link.download = collectionPath;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadEnvironment = () => {
    const envValues = [
      {
        key: "BaseUrl",
        value: credentials?.clientUrl || '',
        type: "default",
        enabled: true
      },
      {
        key: "Azure_AD_Client_ID",
        value: credentials?.clientId || '',
        type: "default",
        enabled: true
      },
      {
        key: "Azure_AD_Client_Secret",
        value: credentials?.clientSecret || '',
        type: "default",
        enabled: true
      },
      {
        key: codeKey,
        value: credentials?.brandCode || '',
        type: "default",
        enabled: true
      },
      {
        key: keyKey,
        value: credentials?.buyNowConsumerKey || '',
        type: "default",
        enabled: true
      }
    ];

    const envFile = {
      id: v4(),
      name: envName,
      values: envValues,
      _postman_variable_scope: "environment",
      _postman_exported_at: new Date().toISOString(),
      _postman_exported_using: "SellerHub/1.0.0"
    };

    const blob = new Blob([JSON.stringify(envFile, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = envFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleAzureAuth = async () => {
    setAzureLoading(true);
    setAzureError('');
    setAzureResponse('');

    try {
      const data = await authenticateAzureAD({
        clientId: azureClientId,
        clientSecret: azureClientSecret,
      }, token || '');

      setAccessToken(data.access_token);
      setAzureResponse(JSON.stringify(data, null, 2));
    } catch (error: any) {
      setAzureError(error.message || 'Failed to authenticate');
    } finally {
      setAzureLoading(false);
    }
  };

  const handleSingleMaterial = async () => {
    if (!accessToken) {
      setSingleMaterialError('Please authenticate with Azure AD first');
      return;
    }

    setSingleMaterialLoading(true);
    setSingleMaterialError('');
    setSingleMaterialResponse('');

    try {
      const params: any = {
        accessToken,
        baseUrl: singleBaseUrl,
        materialNumber,
        country: singleCountry,
        level
      };

      if (level === 'vendor') {
        params.vendorCode = singleBrandCode;
        params.vendorKey = singleBrandKey;
      } else {
        params.brandCode = singleBrandCode;
        params.brandKey = singleBrandKey;
      }

      const data = await fetchSingleMaterial(params, token || '');

      setSingleMaterialResponse(JSON.stringify(data, null, 2));
    } catch (error: any) {
      setSingleMaterialError(error.message || 'Failed to fetch material');
    } finally {
      setSingleMaterialLoading(false);
    }
  };

  const handleBatchMaterial = async () => {
    if (!accessToken) {
      setBatchMaterialError('Please authenticate with Azure AD first');
      return;
    }

    setBatchMaterialLoading(true);
    setBatchMaterialError('');
    setBatchMaterialResponse('');

    try {
      const materialNumbersArray = materialNumbers.split(',').map(num => num.trim());

      const params: any = {
        accessToken,
        baseUrl: batchBaseUrl,
        materialNumbers: materialNumbersArray,
        country: batchCountry,
        level
      };

      if (level === 'vendor') {
        params.vendorCode = batchBrandCode;
        params.vendorKey = batchBrandKey;
      } else {
        params.brandCode = batchBrandCode;
        params.brandKey = batchBrandKey;
      }

      const data = await fetchBatchMaterials(params, token || '');

      setBatchMaterialResponse(JSON.stringify(data, null, 2));
    } catch (error: any) {
      setBatchMaterialError(error.message || 'Failed to fetch materials');
    } finally {
      setBatchMaterialLoading(false);
    }
  };

  const instructionsTab = (
    <FlexboxContainer flexDirection="column" gap="16px">
      <SectionTitle className='heading'>Getting Started with Postman</SectionTitle>
      <InstructionsList>
        <li>
          <strong>Download Postman:</strong> If you don't have Postman installed, download it from{' '}
          <a href={POSTMAN_DOWNLOAD_LINK} target="_blank" rel="noopener noreferrer">
            postman.com/downloads
          </a>.
        </li>
        <li>
          <strong>Download the Files:</strong> Click the buttons below to download both the Collection and Environment files.
        </li>
        <li>
          <strong>Import to Postman:</strong> Open Postman, click "Import", and select both downloaded files.
        </li>
      </InstructionsList>

      <SectionTitle className='heading'>Setting Up Environment</SectionTitle>
      <InstructionsList>
        <li>After importing the environment file, select "{envName}" from the environment dropdown in Postman</li>
        <li>
          The environment file is pre-configured with your credentials:
          <ul style={{ marginTop: '8px' }}>
            <li><code>Azure_AD_Client_ID</code> - Your Azure AD Client ID</li>
            <li><code>Azure_AD_Client_Secret</code> - Your Azure AD Client Secret</li>
            <li><code>BaseUrl</code> - Your API Base URL</li>
            <li><code>{codeKey}</code> - Your {codeLabel}</li>
            <li><code>{keyKey}</code> - Your {keyLabel}</li>
          </ul>
        </li>
      </InstructionsList>

      <SectionTitle className='heading'>Using the Collection</SectionTitle>
      <InstructionsList>
        <li>
          <strong>Step 1:</strong> Run the "Azure AD" request to get an access token (it will automatically save the token).
        </li>
        <li>
          <strong>Step 2:</strong> Before running material requests, replace <code>&lt;Country_ISO_CODE&gt;</code> in the URL with your desired country code (e.g., US).
        </li>
        <li>
          <strong>Step 3:</strong> Run "Single Material" or "Batch Material" requests to fetch material data by providing material numbers.
        </li>
        <li>
          <strong>Note:</strong> All environment variables will be automatically used in the requests.
        </li>
      </InstructionsList>

      <FlexboxContainer justifyContent="center" gap="16px" margin="20px 0 0 0">
        <DownloadButton
          onClick={handleDownloadCollection}
          color={Main.brandDark}
          borderColor={Main.brandDark}
          background='white'
        >
          Download Postman Collection
        </DownloadButton>
        <DownloadButton
          onClick={handleDownloadEnvironment}
          color={Main.brandDark}
          borderColor={Main.brandDark}
          background='white'
        >
          Download Environment File
        </DownloadButton>
      </FlexboxContainer>
    </FlexboxContainer>
  );

  const apiTestingTab = (
    <ScrollableContainer flexDirection="column" gap="16px">
      {/* Azure AD Authentication */}
      <ApiSection>
        <ApiSectionTitle>1. Azure AD Authentication</ApiSectionTitle>
        <FlexboxContainer gap="16px" flexDirection="column">
          <Input
            id="azure-client-id"
            labelName="Client ID"
            value={azureClientId}
            onInputChange={setAzureClientId}
            width="100%"
            placeholder='Client ID'
          />
          <Input
            id="azure-client-secret"
            labelName="Client Secret"
            value={azureClientSecret}
            onInputChange={setAzureClientSecret}
            width="100%"
            placeholder='Client Secret'
          />
          <Button
            onClick={handleAzureAuth}
            disabled={azureLoading || !azureClientId || !azureClientSecret}
            color={Main.brandDark}
            background='white'
            borderColor={Main.brandDark}
          >
            {azureLoading ? 'Authenticating...' : 'Get Access Token'}
          </Button>
          {accessToken && <SuccessMessage>✓ Access token obtained successfully</SuccessMessage>}
          {azureError && <ErrorMessage>{azureError}</ErrorMessage>}
          {azureResponse && <ResponseArea>{azureResponse}</ResponseArea>}
        </FlexboxContainer>
      </ApiSection>

      {/* Single Material */}
      <ApiSection>
        <ApiSectionTitle>2. Single Material</ApiSectionTitle>
        <FlexboxContainer gap="16px" flexDirection="column">
          <Input
            id="single-base-url"
            labelName="Base URL"
            value={singleBaseUrl}
            onInputChange={setSingleBaseUrl}
            width="100%"
            placeholder="Base URL"
          />
          <FlexboxContainer gap="16px" flexDirection="row">
            <Input
              id="single-brand-code"
              labelName={codeLabel}
              value={singleBrandCode}
              onInputChange={setSingleBrandCode}
              width="100%"
              placeholder={codeLabel}
            />
            <Input
              id="single-brand-key"
              labelName={keyLabel}
              value={singleBrandKey}
              onInputChange={setSingleBrandKey}
              width="100%"
              placeholder={keyLabel}
            />
          </FlexboxContainer>
          <FlexboxContainer gap="16px" flexDirection="row">
            <Input
              id="material-number"
              labelName="Material Number"
              value={materialNumber}
              onInputChange={setMaterialNumber}
              width="100%"
              placeholder='Material Number'
            />
            <Input
              id="single-country"
              labelName="Country ISO Code"
              value={singleCountry}
              onInputChange={setSingleCountry}
              width="100%"
              placeholder='e.g., US'
            />
          </FlexboxContainer>
          <div style={{ width: 'fit-content' }}>
            <Tooltip
              content="Authentication is required"
              direction='right'
              hidden={!!accessToken}
            >
              <Button
                onClick={handleSingleMaterial}
                disabled={
                  singleMaterialLoading ||
                  !singleBrandCode ||
                  !singleBrandKey ||
                  !materialNumber ||
                  !singleCountry ||
                  !accessToken ||
                  !singleBaseUrl
                }
                color={Main.brandDark}
                borderColor={Main.brandDark}
                background='white'

              >
                {singleMaterialLoading ? 'Fetching...' : 'Fetch Material'}
              </Button>
            </Tooltip>
          </div>
          {singleMaterialError && <ErrorMessage>{singleMaterialError}</ErrorMessage>}
          {singleMaterialResponse && <ResponseArea>{singleMaterialResponse}</ResponseArea>}
        </FlexboxContainer>
      </ApiSection>

      {/* Batch Material */}
      <ApiSection>
        <ApiSectionTitle>3. Batch Material</ApiSectionTitle>
        <FlexboxContainer gap="16px" flexDirection="column">
          <Input
            id="batch-base-url"
            labelName="Base URL"
            value={batchBaseUrl}
            onInputChange={setBatchBaseUrl}
            width="100%"
            placeholder="Base URL"
          />
          <FlexboxContainer gap="16px" flexDirection="row">
            <Input
              id="batch-brand-code"
              labelName={codeLabel}
              value={batchBrandCode}
              onInputChange={setBatchBrandCode}
              width="100%"
              placeholder={codeLabel}
            />
            <Input
              id="batch-brand-key"
              labelName={keyLabel}
              value={batchBrandKey}
              onInputChange={setBatchBrandKey}
              width="100%"
              placeholder={keyLabel}
            />
          </FlexboxContainer>
          <FlexboxContainer gap="16px" flexDirection="row">
            <Input
              id="material-numbers"
              labelName="Material Numbers (comma-separated)"
              value={materialNumbers}
              onInputChange={setMaterialNumbers}
              width="100%"
              placeholder="e.g., ABC123, XYZ456, DEF789"
            />
            <Input
              id="batch-country"
              labelName="Country ISO Code"
              value={batchCountry}
              onInputChange={setBatchCountry}
              width="100%"
              placeholder='e.g., US'
            />
          </FlexboxContainer>
          <div style={{ width: 'fit-content' }}>
            <Tooltip
              content="Authentication is required"
              direction='right'
              hidden={!!accessToken}
            >
              <Button
                onClick={handleBatchMaterial}
                disabled={
                  batchMaterialLoading ||
                  !batchBrandCode ||
                  !batchBrandKey ||
                  !materialNumbers ||
                  !batchCountry ||
                  !accessToken ||
                  !batchBaseUrl
                }
                color={Main.brandDark}
                borderColor={Main.brandDark}
                background='white'
              >
                {batchMaterialLoading ? 'Fetching...' : 'Fetch Materials'}
              </Button>
            </Tooltip>
          </div>
          {batchMaterialError && <ErrorMessage>{batchMaterialError}</ErrorMessage>}
          {batchMaterialResponse && <ResponseArea>{batchMaterialResponse}</ResponseArea>}
        </FlexboxContainer>
      </ApiSection>
    </ScrollableContainer >
  );

  return (
    <Modal
      showModal={show}
      onClose={onClose}
      title="Buy Now API Configuration"
      width="1000px"
      showCloseIcon={true}
      showButtons={false}
      theme="accent"
      primaryButtonText=""
      ghostButtonText=""
      left='23%'
    >
      <Tabs
        tabs={[
          { value: 'Instructions', badge: { content: '', color: '', backgroundColor: '' } },
          { value: 'API Testing', badge: { content: '', color: '', backgroundColor: '' } }
        ]}
        theme="brandLight"
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      >
        {instructionsTab}
        {apiTestingTab}
      </Tabs>
    </Modal>
  );
};

export default BuyNowConfigModal;
